import React, { useState, useEffect } from "react";
import { spinAPI, vouchersAPI, usersAPI } from "../services/api";
import { Voucher } from "../types/voucher";
import toast from "react-hot-toast";

interface Slot {
  label: string;
  voucherCode: string | null;
  probability: number;
  color: string;
}

interface SpinEvent {
  _id: string;
  name: string;
  description: string;
  slots: Slot[];
  eligibleUsers: "all" | string[];
  spinsPerUser: number;
  startDate: string;
  endDate: string;
  status: "active" | "inactive";
}

interface SpinResult {
  _id: string;
  wonAt: string;
  userId: { name: string; email: string } | null;
  spinEventId: { name: string } | null;
  slotIndex: number;
  voucherCode: string | null;
}

interface UserItem { _id: string; name: string; email: string; phone?: string; }

const PRESET_COLORS = [
  "#FF6B6B","#FFA500","#FFD700","#4CAF50","#2196F3","#9C27B0","#FF69B4","#00BCD4",
];

const defaultSlot = (): Slot => ({ label: "", voucherCode: null, probability: 0, color: PRESET_COLORS[0] });

const emptyForm = (): Omit<SpinEvent, "_id"> => ({
  name: "",
  description: "",
  slots: [
    { label: "Chúc may mắn lần sau", voucherCode: null, probability: 50, color: "#9E9E9E" },
    { label: "Giải thưởng", voucherCode: null, probability: 50, color: PRESET_COLORS[1] },
  ],
  eligibleUsers: "all",
  spinsPerUser: 1,
  startDate: "",
  endDate: "",
  status: "active",
});

export default function SpinPage(): React.ReactElement {
  const [events, setEvents]     = useState<SpinEvent[]>([]);
  const [results, setResults]   = useState<SpinResult[]>([]);
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [users, setUsers]       = useState<UserItem[]>([]);
  const [loading, setLoading]   = useState(true);
  const [tab, setTab]           = useState<"events"|"results">("events");

  const [showModal, setShowModal]     = useState(false);
  const [editing, setEditing]         = useState<SpinEvent | null>(null);
  const [form, setForm]               = useState<Omit<SpinEvent, "_id">>(emptyForm());
  const [saving, setSaving]           = useState(false);
  const [userSearch, setUserSearch]   = useState("");

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    const [evRes, resRes, vRes, uRes] = await Promise.allSettled([
      spinAPI.getAll(),
      spinAPI.getResults(),
      vouchersAPI.getAll(),
      usersAPI.getAll(),
    ]);
    if (evRes.status === "fulfilled")  setEvents(evRes.value.data.data || []);
    if (resRes.status === "fulfilled") setResults(resRes.value.data.data || []);
    if (vRes.status === "fulfilled")   setVouchers(vRes.value.data.data || []);
    if (uRes.status === "fulfilled")   setUsers(uRes.value.data.data || []);
    setLoading(false);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setUserSearch("");
    setShowModal(true);
  };

  const openEdit = (ev: SpinEvent) => {
    setEditing(ev);
    setUserSearch("");
    setForm({
      name: ev.name,
      description: ev.description,
      slots: ev.slots.map(s => ({ ...s })),
      eligibleUsers: ev.eligibleUsers,
      spinsPerUser: ev.spinsPerUser,
      startDate: ev.startDate,
      endDate: ev.endDate,
      status: ev.status,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.startDate || !form.endDate) {
      toast.error("Vui lòng điền đủ thông tin (tên, ngày)");
      return;
    }
    const emptyLabel = form.slots.findIndex(sl => !sl.label.trim());
    if (emptyLabel !== -1) {
      toast.error(`Ô số ${emptyLabel + 1} chưa có tên — vui lòng điền tên cho tất cả các ô`);
      return;
    }
    const total = form.slots.reduce((s, sl) => s + Number(sl.probability), 0);
    if (Math.abs(total - 100) > 0.01) {
      toast.error(`Tổng xác suất đang là ${total.toFixed(1)}%, cần bằng 100%`);
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form } as Record<string, unknown>;
      if (editing) {
        await spinAPI.update(editing._id, payload);
        toast.success("Đã cập nhật sự kiện");
      } else {
        await spinAPI.create(payload);
        toast.success("Đã tạo sự kiện");
      }
      setShowModal(false);
      fetchAll();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Lỗi lưu sự kiện");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Xóa sự kiện này?")) return;
    try {
      await spinAPI.delete(id);
      toast.success("Đã xóa");
      fetchAll();
    } catch {
      toast.error("Lỗi xóa");
    }
  };

  // --- Slot helpers ---
  const updateSlot = (i: number, field: keyof Slot, value: unknown) => {
    setForm(f => {
      const slots = [...f.slots];
      slots[i] = { ...slots[i], [field]: value };
      return { ...f, slots };
    });
  };

  const addSlot = () => setForm(f => ({ ...f, slots: [...f.slots, defaultSlot()] }));

  const removeSlot = (i: number) => {
    if (form.slots.length <= 2) { toast.error("Cần ít nhất 2 ô"); return; }
    setForm(f => ({ ...f, slots: f.slots.filter((_, idx) => idx !== i) }));
  };

  const totalProbability = form.slots.reduce((s, sl) => s + Number(sl.probability || 0), 0);

  // --- Eligible users ---
  const isAllUsers = form.eligibleUsers === "all";
  const selectedUsers: string[] = isAllUsers ? [] : (form.eligibleUsers as string[]);

  const toggleUser = (id: string) => {
    if (isAllUsers) {
      setForm(f => ({ ...f, eligibleUsers: [id] }));
    } else {
      const arr = selectedUsers.includes(id)
        ? selectedUsers.filter(u => u !== id)
        : [...selectedUsers, id];
      setForm(f => ({ ...f, eligibleUsers: arr.length === 0 ? "all" : arr }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">🎡 Quay số may mắn</h1>
        <button
          onClick={openCreate}
          className="px-4 py-2 bg-coffee-600 text-white rounded-lg hover:bg-coffee-700 font-medium"
        >
          + Tạo sự kiện
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        {(["events","results"] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 font-medium text-sm border-b-2 -mb-px transition-colors ${tab === t ? "border-coffee-600 text-coffee-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}
          >
            {t === "events" ? "Sự kiện" : "Lịch sử quay"}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Đang tải...</div>
      ) : tab === "events" ? (
        /* Events list */
        events.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <div className="text-5xl mb-3">🎡</div>
            <p>Chưa có sự kiện nào. Hãy tạo sự kiện đầu tiên!</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {events.map(ev => (
              <div key={ev._id} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-gray-900 text-lg">{ev.name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ev.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                        {ev.status === "active" ? "Hoạt động" : "Tạm dừng"}
                      </span>
                    </div>
                    {ev.description && <p className="text-sm text-gray-500 mt-1">{ev.description}</p>}
                    <p className="text-xs text-gray-400 mt-1">{ev.startDate} → {ev.endDate} · {ev.spinsPerUser} lượt/người</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(ev)} className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">Sửa</button>
                    <button onClick={() => handleDelete(ev._id)} className="px-3 py-1.5 text-sm border border-red-200 text-red-600 rounded-lg hover:bg-red-50">Xóa</button>
                  </div>
                </div>

                {/* Slots preview */}
                <div className="flex flex-wrap gap-2 mt-3">
                  {ev.slots.map((sl, i) => (
                    <span
                      key={i}
                      className="text-xs px-2.5 py-1 rounded-full text-white font-medium"
                      style={{ backgroundColor: sl.color }}
                    >
                      {sl.label} ({sl.probability}%)
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        /* Results table */
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-gray-600 font-medium">Người dùng</th>
                <th className="text-left px-4 py-3 text-gray-600 font-medium">Sự kiện</th>
                <th className="text-left px-4 py-3 text-gray-600 font-medium">Kết quả</th>
                <th className="text-left px-4 py-3 text-gray-600 font-medium">Thời gian</th>
              </tr>
            </thead>
            <tbody>
              {results.length === 0 ? (
                <tr><td colSpan={4} className="text-center py-8 text-gray-400">Chưa có lượt quay nào</td></tr>
              ) : results.map(r => (
                <tr key={r._id} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800">{r.userId?.name || "—"}</div>
                    <div className="text-xs text-gray-400">{r.userId?.email}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{r.spinEventId?.name || "—"}</td>
                  <td className="px-4 py-3">
                    {r.voucherCode
                      ? <span className="text-green-600 font-medium">🎁 {r.voucherCode}</span>
                      : <span className="text-gray-400">Không trúng</span>
                    }
                  </td>
                  <td className="px-4 py-3 text-gray-500">{new Date(r.wonAt).toLocaleString("vi-VN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-lg font-bold">{editing ? "Sửa sự kiện" : "Tạo sự kiện mới"}</h2>
            </div>

            <div className="p-6 space-y-5">
              {/* Basic info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tên sự kiện *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-coffee-500"
                    placeholder="VD: Vòng quay may mắn tháng 4"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ngày bắt đầu *</label>
                  <input type="date" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-coffee-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ngày kết thúc *</label>
                  <input type="date" value={form.endDate} onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-coffee-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Số lượt/người</label>
                  <input type="number" min={1} value={form.spinsPerUser} onChange={e => setForm(f => ({ ...f, spinsPerUser: Number(e.target.value) }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-coffee-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trạng thái</label>
                  <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value as "active"|"inactive" }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-coffee-500">
                    <option value="active">Hoạt động</option>
                    <option value="inactive">Tạm dừng</option>
                  </select>
                </div>
              </div>

              {/* Slots */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Các ô quay
                    <span className={`ml-2 text-xs font-normal ${Math.abs(totalProbability - 100) < 0.01 ? "text-green-600" : "text-red-500"}`}>
                      (Tổng: {totalProbability.toFixed(1)}% / 100%)
                    </span>
                  </label>
                  <button type="button" onClick={addSlot} className="text-xs px-2 py-1 bg-coffee-100 text-coffee-700 rounded-lg hover:bg-coffee-200">
                    + Thêm ô
                  </button>
                </div>

                <div className="space-y-2">
                  {form.slots.map((sl, i) => (
                    <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
                      {/* Color picker */}
                      <div className="relative">
                        <div className="w-7 h-7 rounded-md cursor-pointer border-2 border-white shadow" style={{ backgroundColor: sl.color }} />
                        <input
                          type="color"
                          value={sl.color}
                          onChange={e => updateSlot(i, "color", e.target.value)}
                          className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                        />
                      </div>
                      {/* Label */}
                      <input
                        type="text"
                        value={sl.label}
                        onChange={e => updateSlot(i, "label", e.target.value)}
                        placeholder="Tên ô..."
                        className="flex-1 border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:border-coffee-400"
                      />
                      {/* Voucher */}
                      <select
                        value={sl.voucherCode || ""}
                        onChange={e => updateSlot(i, "voucherCode", e.target.value || null)}
                        className="w-44 border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:border-coffee-400"
                      >
                        <option value="">Không có giải</option>
                        {vouchers.filter(v => v.status === "active").map(v => (
                          <option key={v._id} value={v.code}>
                            {v.code} ({v.discountType === "percentage" ? `-${v.discountValue}%` : `-${v.discountValue.toLocaleString("vi-VN")}đ`})
                          </option>
                        ))}
                      </select>
                      {/* Probability */}
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step={0.5}
                          value={sl.probability}
                          onChange={e => updateSlot(i, "probability", Number(e.target.value))}
                          className="w-16 border border-gray-200 rounded px-2 py-1 text-sm text-center focus:outline-none focus:border-coffee-400"
                        />
                        <span className="text-xs text-gray-400">%</span>
                      </div>
                      {/* Remove */}
                      <button type="button" onClick={() => removeSlot(i)} className="text-gray-300 hover:text-red-400 p-1">✕</button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Eligible users */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Người dùng được quay
                </label>
                <div className="flex gap-3 mb-2">
                  <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <input type="radio" checked={isAllUsers} onChange={() => setForm(f => ({ ...f, eligibleUsers: "all" }))} />
                    Tất cả người dùng
                  </label>
                  <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <input type="radio" checked={!isAllUsers} onChange={() => setForm(f => ({ ...f, eligibleUsers: [] }))} />
                    Chọn người dùng cụ thể
                  </label>
                </div>
                {!isAllUsers && (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="p-2 border-b border-gray-100">
                      <input
                        type="text"
                        value={userSearch}
                        onChange={e => setUserSearch(e.target.value)}
                        placeholder="Tìm theo tên hoặc email..."
                        className="w-full text-sm px-2 py-1.5 border border-gray-200 rounded focus:outline-none focus:border-coffee-400"
                      />
                    </div>
                    <div className="max-h-40 overflow-y-auto p-2 space-y-1">
                      {users
                        .filter(u => {
                          const q = userSearch.toLowerCase();
                          if (!q) return true;
                          return (
                            u.name.toLowerCase().includes(q) ||
                            u.email.toLowerCase().includes(q) ||
                            (u.phone || "").includes(q)
                          );
                        })
                        .map(u => (
                          <label key={u._id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5">
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(u._id)}
                              onChange={() => toggleUser(u._id)}
                            />
                            <span className="font-medium">{u.name}</span>
                            <span className="text-gray-400 text-xs">{u.email}</span>
                            {u.phone && <span className="text-gray-400 text-xs">{u.phone}</span>}
                          </label>
                        ))
                      }
                      {users.length === 0 && <p className="text-xs text-gray-400 p-2">Không có người dùng</p>}
                    </div>
                    {selectedUsers.length > 0 && (
                      <div className="px-3 py-1.5 bg-coffee-50 border-t border-gray-100 text-xs text-coffee-700 font-medium">
                        Đã chọn {selectedUsers.length} người dùng
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t flex justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
                Hủy
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-coffee-600 text-white rounded-lg text-sm hover:bg-coffee-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving && <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
                {editing ? "Lưu thay đổi" : "Tạo sự kiện"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
